import { useState, useEffect } from 'react';
import { Share, Plus, X } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already installed/standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) {
        return; // Don't show if dismissed within 7 days
      }
    }

    if (standalone) {
      return; // Already installed
    }

    // For iOS, show instructions after delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // For Android/Desktop, listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  // iOS-specific install instructions
  if (isIOS) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        border: '1px solid #10b981',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        zIndex: 9999,
        maxWidth: '90%',
        width: '340px'
      }}>
        {/* Close button */}
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'transparent',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '2.5rem' }}>📱</span>
          <div style={{ fontWeight: 'bold', color: 'white', fontSize: '1.1rem', marginTop: '8px' }}>
            Install Savage AI Signals
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '4px' }}>
            Add to your home screen for quick access
          </div>
        </div>

        {/* Instructions */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ fontWeight: '600', color: '#10b981', marginBottom: '12px', fontSize: '0.9rem' }}>
            How to install on iPhone:
          </div>
          
          {/* Step 1 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              flexShrink: 0
            }}>1</div>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>
              Tap the <Share size={16} style={{ display: 'inline', verticalAlign: 'middle', color: '#3b82f6' }} /> <strong style={{ color: '#3b82f6' }}>Share</strong> button <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>(bottom in Safari, top in Chrome)</span>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              flexShrink: 0
            }}>2</div>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>
              Scroll down and tap <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', background: '#374151', borderRadius: '4px', padding: '2px' }} /> <strong>"Add to Home Screen"</strong>
            </div>
          </div>

          {/* Step 3 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.85rem',
              flexShrink: 0
            }}>3</div>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem' }}>
              Tap <strong>"Add"</strong> in the top right corner
            </div>
          </div>
        </div>

        {/* Note */}
        <div style={{ 
          fontSize: '0.75rem', 
          color: '#64748b', 
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          💡 Works best with <strong>Safari</strong> or <strong>Chrome</strong>
        </div>
      </div>
    );
  }

  // Android/Desktop install prompt
  if (!deferredPrompt) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      border: '1px solid #10b981',
      borderRadius: '12px',
      padding: '16px 20px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
      zIndex: 9999,
      maxWidth: '90%',
      width: '360px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <span style={{ fontSize: '2rem' }}>📱</span>
        <div>
          <div style={{ fontWeight: 'bold', color: 'white', fontSize: '1rem' }}>
            Install Savage AI Signals
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
            Add to home screen for quick access
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleInstall}
          style={{
            flex: 1,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Install App
        </button>
        <button
          onClick={handleDismiss}
          style={{
            background: 'transparent',
            color: '#94a3b8',
            border: '1px solid #334155',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Later
        </button>
      </div>
    </div>
  );
}
